/**
 * Scenarios – ScenarioDetailPanel
 *
 * Right-hand panel: loads the full scenario, renders tabs (Drivers, Monthly/FY,
 * Reconciliation, Audit), and hosts the Add Driver form and Promote dialog.
 *
 * Scope is passed in so promote + recompute cache invalidation stays consistent
 * with the page-level list query key.
 */

import { useState, useCallback } from "react";
import { AlertCircle, ArrowRight, Layers, Lock, Loader2, Plus, RefreshCw, X } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetScenario,
  useAddScenarioDriver,
  useRecomputeScenario,
  getGetScenarioQueryKey,
  getListScenariosQueryKey,
} from "@workspace/api-client-react";
import { PermissionGate } from "@/framework/microapp-context";
import type {
  ScenarioDriverInput,
  ScenarioPromotion,
  ScenarioQuestionTemplate,
} from "@workspace/api-client-react";
import { ACTOR } from "./types";
import type { ScenarioScope } from "./types";
import { StatusBadge, ReconciliationStatus } from "./shared";
import { ImpactKpis, MonthlyResultsTable, ReconciliationPanel } from "./results";
import { DriverForm, DriversList } from "./driver-form";
import { LicensingAnalytics } from "./licensing-analytics";
import { AuditTrail } from "./audit";
import { PromoteDialog, PromotedBanner } from "./promotion";
import { QuestionPlaybook, InvestmentSensitivity } from "./guidance";
import { friendlyScenarioTitle } from "./utils";

type DetailTab =
  | "drivers"
  | "monthly"
  | "licensing"
  | "reconciliation"
  | "audit";

export function ScenarioDetailPanel({
  scenarioId,
  templates,
  scope,
  onClose,
}: {
  scenarioId: string;
  templates: ScenarioQuestionTemplate[];
  scope: ScenarioScope;
  onClose: () => void;
}) {
  const [activeTab, setActiveTab] = useState<DetailTab>("drivers");
  const [showDriverForm, setShowDriverForm] = useState(false);
  const [driverFormError, setDriverFormError] = useState<string>("");
  const [promotionResult, setPromotionResult] = useState<ScenarioPromotion | null>(null);
  const [showPromoteDialog, setShowPromoteDialog] = useState(false);
  const qc = useQueryClient();

  const { data: scenario, isLoading, isError, refetch } = useGetScenario(
    scenarioId,
    {
      query: {
        queryKey: getGetScenarioQueryKey(scenarioId),
        enabled: !!scenarioId,
      },
    }
  );

  const addDriverMutation = useAddScenarioDriver();
  const recomputeMutation = useRecomputeScenario();

  const template = templates.find(
    (t) => scenario && t.key === scenario.questionKey
  );
  const isPromoted = scenario?.status === "promoted";
  const hasDrivers = (scenario?.drivers?.length ?? 0) > 0;

  const hasLicensing =
    (scenario?.licensingAnalytics?.length ?? 0) > 0 ||
    (scenario?.licensingMonthlyComponents?.length ?? 0) > 0 ||
    (scenario?.drivers?.some((d) => d.type === "licensing_model_change") ??
      false);

  const tabs: Array<{ key: DetailTab; label: string; testId: string }> = [
    { key: "drivers", label: "Drivers", testId: "tab-drivers" },
    { key: "monthly", label: "Monthly / FY", testId: "tab-monthly" },
    ...(hasLicensing
      ? [
          {
            key: "licensing" as DetailTab,
            label: "Licensing",
            testId: "tab-licensing",
          },
        ]
      : []),
    {
      key: "reconciliation",
      label: "Reconciliation",
      testId: "tab-reconciliation",
    },
    { key: "audit", label: "Audit", testId: "tab-audit" },
  ];

  const handleAddDriver = useCallback(
    (driver: ScenarioDriverInput) => {
      setDriverFormError("");
      addDriverMutation.mutate(
        { scenarioId, data: driver },
        {
          onSuccess: (updated) => {
            qc.setQueryData(getGetScenarioQueryKey(scenarioId), updated);
            qc.invalidateQueries({
              queryKey: getListScenariosQueryKey(scope),
            });
            setShowDriverForm(false);
          },
          onError: (err: unknown) => {
            const msg =
              err && typeof err === "object" && "message" in err
                ? String((err as { message: string }).message)
                : "Failed to add driver. Check that source references are valid if required.";
            setDriverFormError(msg);
          },
        }
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scenarioId, qc]
  );

  const handleRecompute = () => {
    recomputeMutation.mutate(
      { scenarioId, data: { actor: ACTOR } },
      {
        onSuccess: (updated) => {
          qc.setQueryData(getGetScenarioQueryKey(scenarioId), updated);
          qc.invalidateQueries({
            queryKey: getListScenariosQueryKey(scope),
          });
        },
      }
    );
  };

  // ── Loading / error states ───────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div
        className="sc-detail-panel sc-detail-panel--loading"
        data-testid="detail-panel-loading"
      >
        <Loader2 size={20} className="animate-spin" />
        <span>Loading scenario…</span>
      </div>
    );
  }

  if (isError || !scenario) {
    return (
      <div className="sc-detail-panel" data-testid="detail-panel-error">
        <div className="sc-form-error" role="alert">
          <AlertCircle size={14} />
          <div>
            <strong>Could not load scenario.</strong>
            <button
              className="tl-btn"
              onClick={() => refetch()}
              style={{ marginTop: 8 }}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sc-detail-panel" data-testid={`detail-panel-${scenarioId}`}>
      {/* Promote dialog (portal-style overlay) */}
      {showPromoteDialog && (
        <PromoteDialog
          scenario={scenario}
          scope={scope}
          onClose={() => setShowPromoteDialog(false)}
          onSuccess={(p) => {
            setPromotionResult(p);
            setShowPromoteDialog(false);
          }}
        />
      )}

      {/* Post-promotion success banner */}
      {promotionResult && (
        <PromotedBanner
          promotion={promotionResult}
          onDismiss={() => setPromotionResult(null)}
        />
      )}

      {/* Panel header */}
      <div className="sc-detail-panel__head">
        <div className="sc-detail-panel__title">
          <div>
            <div className="sc-detail-panel__eyebrow">
              {template?.question ?? scenario.questionKey}
            </div>
            <h2 data-testid="detail-panel-name">
              {friendlyScenarioTitle(scenario, template?.question)}
            </h2>
            {scenario.description && (
              <p className="sc-detail-panel__desc">{scenario.description}</p>
            )}
          </div>
          <div className="sc-detail-panel__title-actions">
            <StatusBadge status={scenario.status} />
            <button
              className="sc-icon-btn"
              onClick={onClose}
              aria-label="Close detail panel"
              data-testid="button-close-detail-panel"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Baseline identity strip */}
        <div
          className="sc-baseline-identity"
          data-testid="detail-baseline-identity"
        >
          <span>
            Baseline: FY{scenario.fiscalYear} · {scenario.departmentCode} · run{" "}
            <code>{scenario.baselineForecastRunId.slice(0, 8)}…</code>
          </span>
          <ReconciliationStatus
            reconciled={scenario.reconciled}
            difference={scenario.reconciliationDifference}
          />
        </div>

        {/* Impact KPIs */}
        <ImpactKpis scenario={scenario} />

        {/* Action bar — hidden for promoted scenarios */}
        {!isPromoted && (
          <div className="sc-detail-actions" data-testid="detail-panel-actions">
            <button
              className="tl-btn"
              onClick={handleRecompute}
              disabled={recomputeMutation.isPending}
              data-testid="button-recompute"
            >
              {recomputeMutation.isPending ? (
                <>
                  <Loader2 size={12} className="animate-spin" /> Recomputing…
                </>
              ) : (
                <>
                  <RefreshCw size={12} /> Recompute
                </>
              )}
            </button>
            <PermissionGate permission="BvA31:publish">
              <button
                className="tl-btn primary"
                onClick={() => setShowPromoteDialog(true)}
                disabled={!scenario.reconciled}
                title={
                  !scenario.reconciled
                    ? "Reconcile scenario before promoting"
                    : "Promote this scenario to a working Forecast for Review"
                }
                data-testid="button-promote-scenario"
              >
                <ArrowRight size={12} />
                Promote to Review
              </button>
            </PermissionGate>
          </div>
        )}

        {isPromoted && (
          <div
            className="sc-promoted-state"
            data-testid="promoted-immutable-notice"
          >
            <Lock size={12} />
            This scenario has been promoted. It is now immutable — navigate to
            Review / Forecast to continue.
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="sc-tabs" role="tablist" data-testid="detail-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            role="tab"
            className={`sc-tab ${
              activeTab === tab.key ? "sc-tab--active" : ""
            }`}
            onClick={() => setActiveTab(tab.key)}
            aria-selected={activeTab === tab.key}
            data-testid={tab.testId}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div
        className="sc-tab-content"
        data-testid={`tab-content-${activeTab}`}
      >
        {/* Drivers tab */}
        {activeTab === "drivers" && (
          <>
            {/* Step-by-step guidance for this management question */}
            <QuestionPlaybook
              questionKey={scenario.questionKey}
              question={template?.question}
            />

            {/* Demo-friendly FY / Rolling 12M sensitivity explainer */}
            <InvestmentSensitivity scenario={scenario} />

            {!isPromoted && (
              <div className="sc-tab-content__toolbar">
                {!showDriverForm && (
                  <button
                    className="tl-btn primary"
                    onClick={() => setShowDriverForm(true)}
                    data-testid="button-add-driver"
                  >
                    <Plus size={12} />
                    Add driver
                  </button>
                )}
              </div>
            )}

            {showDriverForm && template && !isPromoted && (
              <DriverForm
                allowedTypes={template.allowedDriverTypes}
                questionKey={scenario.questionKey}
                scope={scope}
                onSubmit={handleAddDriver}
                onCancel={() => {
                  setShowDriverForm(false);
                  setDriverFormError("");
                }}
                isPending={addDriverMutation.isPending}
                error={driverFormError}
              />
            )}

            {showDriverForm && !template && !isPromoted && (
              <div className="sc-form-error">
                <AlertCircle size={13} />
                <span>
                  Template not found for this scenario. Cannot add drivers.
                </span>
              </div>
            )}

            {/* Drivers: list when present, else a clear empty state with the
                exact next action tuned to editable vs promoted. */}
            {hasDrivers ? (
              <DriversList scenario={scenario} />
            ) : isPromoted ? (
              <div
                className="sc-drivers-empty sc-drivers-empty--locked"
                data-testid="drivers-empty-promoted"
              >
                <Lock size={20} />
                <h4>No drivers were recorded before promotion</h4>
                <p>
                  This scenario has been promoted, so its drivers are locked and
                  cannot be changed. There are no drivers to display. Review the
                  promoted Forecast for Review to see the assumptions carried
                  forward.
                </p>
              </div>
            ) : (
              <div
                className="sc-drivers-empty"
                data-testid="drivers-empty"
              >
                <Layers size={20} />
                <h4>No drivers yet</h4>
                <p>
                  Add a driver to model this scenario&apos;s assumptions. Follow
                  the steps above to choose the right driver, then FY and Rolling
                  12-month impact will be calculated on recompute.
                </p>
                {!showDriverForm && (
                  <button
                    className="tl-btn primary"
                    onClick={() => setShowDriverForm(true)}
                    data-testid="button-add-first-driver"
                  >
                    <Plus size={12} />
                    Add first driver
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {/* Monthly / FY tab */}
        {activeTab === "monthly" && (
          <MonthlyResultsTable scenario={scenario} />
        )}

        {/* Licensing tab */}
        {activeTab === "licensing" && (
          <LicensingAnalytics scenario={scenario} />
        )}

        {/* Reconciliation tab */}
        {activeTab === "reconciliation" && (
          <ReconciliationPanel scenario={scenario} />
        )}

        {/* Audit tab */}
        {activeTab === "audit" && <AuditTrail scenarioId={scenarioId} />}
      </div>
    </div>
  );
}
